// src/features/plan/RecommendationsPane.jsx
//
// PLAN-RECOMMENDATIONS-TAB: The full recommendations view mounted when
// planView === "recommendations" in MarketPlanPage.
//
// Layout:
//   [Query row]        — Search · Filter · Sort (editor/admin only, above candidate queue)
//   [Candidate queue]  — editor/admin only. Cards with Accept/Edit/Snooze/Dismiss.
//   [Published]        — all roles. Accepted cards in rank order with hand-off buttons.
//
// Viewers see only the published section.
// Empty states are written and explain what would put data there.

import React, { useState, useCallback, useEffect, useMemo } from "react";
import { T } from "../../app/tokens.js";
import { RecommendationCard } from "./RecommendationCard.jsx";
import {
  getRecommendations,
  curateRecommendation,
  unpublishRecommendation,
} from "./recommendationsClient.js";
import { getMarkets } from "../../data/marketsClient.js";
import { SearchInput } from "../../v2/components/SearchInput.jsx";
import { matchesFoldedQuery } from "../../utils/textSearch.js";
import { ConsolidatedFilter } from "../../v2/components/ConsolidatedFilter.jsx";
import { SortControl } from "../../v2/components/SortControl.jsx";

// ─── Filter + Sort config ──────────────────────────────────────────────────────

const REC_TYPE_OPTIONS = [
  { value: "STRATEGY", label: "Strategy" },
  { value: "PRESSURE", label: "Pressure" },
  { value: "LEASE",    label: "Lease"    },
  { value: "PLAN",     label: "Plan"     },
];

// Recommendation status values that exist in the DB — "candidate" (in queue),
// "snoozed" (user-deferred), "dismissed" (user-rejected). "accepted" rows
// appear only in the published section and are not shown in the candidate queue.
const REC_STATUS_OPTIONS = [
  { value: "candidate", label: "In queue"  },
  { value: "snoozed",   label: "Snoozed"   },
  { value: "dismissed", label: "Dismissed" },
];

// Confidence is a coarse three-level signal derived from rec.priority:
//   act_now      → high   (deadline-driven, high-urgency signal)
//   this_quarter → med    (near-term planning horizon)
//   plan_ahead   → low    (longer-range, strategic horizon)
const REC_CONFIDENCE_OPTIONS = [
  { value: "high", label: "High" },
  { value: "med",  label: "Med"  },
  { value: "low",  label: "Low"  },
];

const PRIORITY_TO_CONFIDENCE = {
  act_now:      "high",
  this_quarter: "med",
  plan_ahead:   "low",
};

const REC_FILTER_DIMENSIONS = [
  { id: "type",       label: "Signal type", options: REC_TYPE_OPTIONS    },
  { id: "status",     label: "Status",      options: REC_STATUS_OPTIONS  },
  { id: "confidence", label: "Confidence",  options: REC_CONFIDENCE_OPTIONS },
];

const REC_SORT_FIELDS = [
  { value: "rank", label: "Priority (default)" },
  { value: "type", label: "Signal type"        },
];

// Priority order used for rank/priority sort (lower index = higher urgency).
const PRIORITY_ORDER = { act_now: 1, this_quarter: 2, plan_ahead: 3 };

// ─── Derived candidate list ───────────────────────────────────────────────────

function deriveVisibleCandidates(candidates, search, filterSelections, sortKey, sortDir) {
  let result = candidates;

  // Search — title, why-line, and market ID from triggerFacts.
  const q = search.trim();
  if (q) {
    result = result.filter((rec) => {
      return matchesFoldedQuery(q, rec.title, rec.why, rec.triggerFacts?.marketId);
    });
  }

  // Dimension filters — AND across dimensions, OR within each dimension.
  const { type, status, confidence } = filterSelections;
  if (type?.length > 0) {
    result = result.filter((rec) =>
      (rec.sourceLenses ?? []).some((l) => type.includes(l))
    );
  }
  if (status?.length > 0) {
    result = result.filter((rec) => status.includes(rec.status));
  }
  if (confidence?.length > 0) {
    // Confidence is derived from priority: act_now=high, this_quarter=med, plan_ahead=low.
    result = result.filter((rec) =>
      confidence.includes(PRIORITY_TO_CONFIDENCE[rec.priority] ?? "low")
    );
  }

  // Sort.
  const copy = [...result];
  const dir  = sortDir === "desc" ? -1 : 1;

  if (sortKey === "type") {
    copy.sort((a, b) => {
      const ta = (a.sourceLenses?.[0] ?? "").toLowerCase();
      const tb = (b.sourceLenses?.[0] ?? "").toLowerCase();
      return ta.localeCompare(tb) * dir;
    });
  } else {
    // Default: sort by priority urgency order.
    copy.sort((a, b) => {
      const pa = PRIORITY_ORDER[a.priority] ?? 4;
      const pb = PRIORITY_ORDER[b.priority] ?? 4;
      return (pa - pb) * dir;
    });
  }

  return copy;
}

// ─── Hand-off buttons ─────────────────────────────────────────────────────────

/**
 * Buttons for accepted cards: Open in Plan, Test in Scenario,
 * Create project, and Start workflow.
 *
 * When a target requires market context and the recommendation has none,
 * the button is disabled AND an explicit note is rendered inline (not just
 * a tooltip, which is inaccessible on touch devices).
 */
function HandOffButtons({ rec, onNavigate }) {
  const facts     = rec.triggerFacts ?? {};
  const marketId  = facts.marketId ?? null;
  const directiveId = facts.directiveId ?? null;

  const buttons = [
    {
      label:     "Open in Plan",
      testId:    "handoff-plan",
      available: !!marketId,
      // In-app navigation via onNavigate callback — avoids localStorage
      // restoring the recommendations view on reload.
      onClick:   () => onNavigate("plan", { marketId }),
    },
    {
      label:     "Test in Scenario",
      testId:    "handoff-scenario",
      available: !!marketId,
      // In-app navigation via onNavigate callback for the same reason.
      onClick:   () => onNavigate("whatif", { marketId }),
    },
    {
      label:     "Create project",
      testId:    "handoff-project",
      available: !!marketId,
      // Deep-link to projects view with the market pre-selected via URL param.
      onClick:   () => {
        const url = new URL(window.location.href);
        url.searchParams.set("view", "projects");
        if (marketId) url.searchParams.set("market", marketId);
        window.location.assign(url.toString());
      },
    },
    {
      label:     "Start action",
      testId:    "handoff-workflow",
      // Workflows can be started without a specific market context.
      available: true,
      onClick:   () => {
        const url = new URL(window.location.href);
        url.searchParams.set("view", "workflows");
        if (marketId) url.searchParams.set("market", marketId);
        if (directiveId) url.searchParams.set("directive", directiveId);
        window.location.assign(url.toString());
      },
    },
  ];

  const anyUnavailable = buttons.some((b) => !b.available);

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {buttons.map((btn) => (
          <button
            key={btn.testId}
            type="button"
            data-testid={`rec-handoff-${rec.id}-${btn.testId}`}
            onClick={btn.available ? btn.onClick : undefined}
            disabled={!btn.available}
            style={{
              background:   "transparent",
              border:       `1px solid ${btn.available ? T.blue : T.borderDark}`,
              borderRadius: 6,
              color:        btn.available ? T.blue : T.borderDark,
              fontSize:     T.fsSm,
              cursor:       btn.available ? "pointer" : "not-allowed",
              padding:      "4px 10px",
              opacity:      btn.available ? 1 : 0.55,
            }}
          >
            {btn.label}
          </button>
        ))}
      </div>
      {/* Explicit inline note for unavailable actions — visible on all devices,
          not relying on hover-only tooltips. */}
      {anyUnavailable && (
        <div
          data-testid={`rec-handoff-${rec.id}-unavailable-note`}
          style={{ marginTop: 4, fontSize: T.fsXxs, color: T.mid, fontStyle: "italic" }}
        >
          Some actions require market context not available for this recommendation type.
        </div>
      )}
    </div>
  );
}

// ─── Section header ───────────────────────────────────────────────────────────

function SectionHeader({ title, count }) {
  return (
    <div
      style={{
        fontSize:     T.fsBase,
        fontWeight:   T.fwSemibold,
        color:        T.dark,
        marginBottom: 10,
        display:      "flex",
        alignItems:   "center",
        gap:          8,
      }}
    >
      {title}
      {count != null && (
        <span
          style={{
            fontSize:     T.fsXs,
            fontWeight:   T.fwBold,
            color:        T.mid,
            background:   T.lightGray,
            borderRadius: "100px",
            padding:      "1px 7px",
          }}
        >
          {count}
        </span>
      )}
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyState({ message }) {
  return (
    <div
      style={{
        padding:    "20px 0",
        color:      T.mid,
        fontSize:   T.fsSm,
        fontStyle:  "italic",
        textAlign:  "center",
      }}
    >
      {message}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * @param {object}   props
 * @param {string}   props.userRole       — "viewer" | "editor" | "admin"
 * @param {Function} props.onNavigate     — (view, params) => void  (supplied by MarketPlanPage)
 */
export function RecommendationsPane({ userRole, onNavigate }) {
  const canEdit = userRole === "editor" || userRole === "admin";

  const [candidates,  setCandidates]  = useState([]);
  const [published,   setPublished]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [nameMap,     setNameMap]     = useState(() => new Map());

  // ── Query row state (candidates only) ──────────────────────────────────────
  const [search,           setSearch]           = useState("");
  const [filterSelections, setFilterSelections] = useState({});
  const [sortKey,          setSortKey]          = useState("rank");
  const [sortDir,          setSortDir]          = useState("asc");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, markets] = await Promise.all([
        getRecommendations(),
        getMarkets().catch(() => []),
      ]);
      setCandidates(data.candidates ?? []);
      setPublished(data.published   ?? []);
      setNameMap(new Map((markets ?? []).map((m) => [m.id.toLowerCase(), m.name])));
    } catch (err) {
      setError(err.message ?? "Failed to load recommendations.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Derived filtered + sorted candidate list ───────────────────────────────
  const visibleCandidates = useMemo(
    () => deriveVisibleCandidates(candidates, search, filterSelections, sortKey, sortDir),
    [candidates, search, filterSelections, sortKey, sortDir]
  );

  // ── Curation handlers ──────────────────────────────────────────────────────

  const handleAccept = useCallback(async (id) => {
    await curateRecommendation(id, "accept");
    await load();
  }, [load]);

  const handleDismiss = useCallback(async (id, dismissReason) => {
    await curateRecommendation(id, "dismiss", { dismissReason });
    await load();
  }, [load]);

  const handleSnooze = useCallback(async (id, snoozeUntil) => {
    await curateRecommendation(id, "snooze", { snoozeUntil });
    await load();
  }, [load]);

  const handleEdit = useCallback(async (id, editFields) => {
    await curateRecommendation(id, "edit", { editFields });
    await load();
  }, [load]);

  const handleUnpublish = useCallback(async (id) => {
    await unpublishRecommendation(id);
    await load();
  }, [load]);

  const handleNavigate = useCallback((view, params) => {
    if (onNavigate) onNavigate(view, params);
  }, [onNavigate]);

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ padding: "24px 0", color: T.mid, fontSize: T.fsSm }}>
        Loading recommendations…
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "16px 0", color: T.ragRed, fontSize: T.fsSm }}>
        {error}
      </div>
    );
  }

  return (
    <div
      data-testid="recommendations-pane"
      style={{ paddingTop: 16, paddingBottom: 32 }}
    >
      {/* ── Candidate queue (editor/admin only) ──────────────────────── */}
      {canEdit && (
        <div
          data-testid="recommendations-candidate-section"
          style={{ marginBottom: 32 }}
        >
          {/* Query row: Search · Filter · Sort */}
          <div
            data-testid="recommendations-query-row"
            style={{
              display:      "flex",
              alignItems:   "center",
              gap:          8,
              marginBottom: 12,
            }}
          >
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search recommendations…"
              testId="rec-search"
            />
            <ConsolidatedFilter
              testId="rec-filter-btn"
              dimensions={REC_FILTER_DIMENSIONS}
              selections={filterSelections}
              onChange={setFilterSelections}
              onClear={() => {}}
            />
            <SortControl
              testId="rec-sort"
              fields={REC_SORT_FIELDS}
              primary={{ key: sortKey, dir: sortDir }}
              secondary={{ key: null, dir: "asc" }}
              defaultKey="rank"
              onChange={({ primary }) => {
                setSortKey(primary.key);
                setSortDir(primary.dir);
              }}
            />
          </div>

          <SectionHeader title="Candidate queue" count={visibleCandidates.length} />
          {visibleCandidates.length === 0 ? (
            <EmptyState message={
              candidates.length === 0
                ? "No candidates in the queue. All rule-triggered signals have been curated."
                : "No candidates match the current search or filters."
            } />
          ) : (
            visibleCandidates.map((rec, idx) => (
              <RecommendationCard
                key={rec.id}
                rank={idx + 1}
                rec={rec}
                canEdit={canEdit}
                nameMap={nameMap}
                onAccept={handleAccept}
                onDismiss={handleDismiss}
                onSnooze={handleSnooze}
                onEdit={handleEdit}
                onUnpublish={handleUnpublish}
                showPublished={false}
                testIdPrefix="candidate-card"
              />
            ))
          )}
        </div>
      )}

      {/* ── Published recommendations ─────────────────────────────────── */}
      <div data-testid="recommendations-published-section">
        <SectionHeader title="Published recommendations" count={published.length} />
        {published.length === 0 ? (
          <EmptyState message="No recommendations published yet. Curators accept candidates from the queue above." />
        ) : (
          published.map((rec, idx) => (
            <div key={rec.id}>
              <RecommendationCard
                rank={idx + 1}
                rec={rec}
                canEdit={canEdit}
                nameMap={nameMap}
                onAccept={handleAccept}
                onDismiss={handleDismiss}
                onSnooze={handleSnooze}
                onEdit={handleEdit}
                onUnpublish={handleUnpublish}
                showPublished={true}
                testIdPrefix="published-card"
              />
              <HandOffButtons rec={rec} onNavigate={handleNavigate} />
            </div>
          ))
        )}
      </div>
    </div>
  );
}
